import { AxiosError, AxiosResponse, isCancel } from 'axios';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  AgentAdoptionParams,
  AgentAdoptionResponse,
  AgentDashboardParams,
  AgentDashboardResponse,
  AgentMinerSessionDetail,
  AgentMinerSessionFacets,
  AgentMinerSessionListParams,
  AgentMinerSessionsResponse,
  DeliveryImpactParams,
  DeliveryImpactResponse,
  ModelComparisonParams,
  ModelComparisonResponse,
  PullRequestFileLinesResponse,
  SessionPullRequestsResponse,
  TaskFitParams,
  TaskFitResponse,
  TaskFitTasksParams,
  TaskFitTasksResponse,
} from './agent-miner-client.type';

const axiosInstance = AxiosInstance();
const dashboardURL = appConfig.VITE_APP_API_URL + '/api/agent-miner/dashboard/';
const modelComparisonURL = appConfig.VITE_APP_API_URL + '/api/agent-miner/model-comparison/';
const deliveryImpactURL = appConfig.VITE_APP_API_URL + '/api/agent-miner/delivery-impact/';
const taskFitURL = appConfig.VITE_APP_API_URL + '/api/agent-miner/task-fit/';
const taskFitTasksURL = taskFitURL + 'tasks/';
const adoptionURL = appConfig.VITE_APP_API_URL + '/api/agent-miner/adoption/';
const sessionsURL = appConfig.VITE_APP_API_URL + '/api/agent-sessions/';

/**
 * Fetches per-model-family delivery metrics for the Model Comparison tab.
 *
 * @param {ModelComparisonParams} params - Organization and date window
 * @param {AbortSignal} signal - Optional abort signal from React Query
 * @returns {Promise<ModelComparisonResponse>} Meta, summary, series, rows, and families
 */
const getModelComparison = async (
  params: ModelComparisonParams,
  signal?: AbortSignal,
): Promise<ModelComparisonResponse> => {
  try {
    const response: AxiosResponse<ModelComparisonResponse> = await axiosInstance.get(modelComparisonURL, {
      params,
      signal,
    });
    return response.data;
  } catch (error: unknown) {
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching model comparison data.'));
    }
  }
};

/**
 * Fetches AI-assisted vs baseline task delivery metrics for the Delivery
 * Impact tab.
 *
 * @param {DeliveryImpactParams} params - Organization and date window
 * @param {AbortSignal} signal - Optional abort signal from React Query
 * @returns {Promise<DeliveryImpactResponse>} Meta, summary, metrics, and cycle-time series
 */
const getDeliveryImpact = async (
  params: DeliveryImpactParams,
  signal?: AbortSignal,
): Promise<DeliveryImpactResponse> => {
  try {
    const response: AxiosResponse<DeliveryImpactResponse> = await axiosInstance.get(deliveryImpactURL, {
      params,
      signal,
    });
    return response.data;
  } catch (error: unknown) {
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching delivery impact data.'));
    }
  }
};

/**
 * Fetches the task-type x model-family rework-rate matrix for the Task Fit tab.
 *
 * @param {TaskFitParams} params - Organization, date window, and row dimension
 * @param {AbortSignal} signal - Optional abort signal from React Query
 * @returns {Promise<TaskFitResponse>} Meta, summary, columns, rows, and recommendations
 */
const getTaskFit = async (params: TaskFitParams, signal?: AbortSignal): Promise<TaskFitResponse> => {
  try {
    const response: AxiosResponse<TaskFitResponse> = await axiosInstance.get(taskFitURL, {
      params,
      signal,
    });
    return response.data;
  } catch (error: unknown) {
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching task fit data.'));
    }
  }
};

/**
 * Fetches the tasks behind one Task Fit matrix cell for the drill-down panel.
 *
 * @param {TaskFitTasksParams} params - Organization, date window, and the cell address
 * @param {AbortSignal} signal - Optional abort signal from React Query
 * @returns {Promise<TaskFitTasksResponse>} Meta counters and the cell's tasks
 */
const getTaskFitTasks = async (params: TaskFitTasksParams, signal?: AbortSignal): Promise<TaskFitTasksResponse> => {
  try {
    const response: AxiosResponse<TaskFitTasksResponse> = await axiosInstance.get(taskFitTasksURL, {
      params,
      signal,
    });
    return response.data;
  } catch (error: unknown) {
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching task fit cell tasks.'));
    }
  }
};

/**
 * Fetches team-based agent adoption metrics for the Adoption tab.
 *
 * Team ids are comma-joined on the wire (UUIDs never contain commas) and the
 * param is omitted entirely when the selection is empty so "no filter" and
 * "all teams" share one cache entry.
 *
 * @param {AgentAdoptionParams} params - Organization, date window, and optional team scope
 * @param {AbortSignal} signal - Optional abort signal from React Query
 * @returns {Promise<AgentAdoptionResponse>} Meta, summary, series, team rows, and attribution blocks
 */
const getAgentAdoption = async (params: AgentAdoptionParams, signal?: AbortSignal): Promise<AgentAdoptionResponse> => {
  const { team_ids, ...scalarParams } = params;
  const query: Record<string, string | undefined> = {
    ...scalarParams,
    ...(team_ids && team_ids.length > 0 ? { team_ids: team_ids.join(',') } : {}),
  };
  try {
    const response: AxiosResponse<AgentAdoptionResponse> = await axiosInstance.get(adoptionURL, {
      params: query,
      signal,
    });
    return response.data;
  } catch (error: unknown) {
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching agent adoption data.'));
    }
  }
};

/**
 * Fetches agent dashboard data with measure values over time.
 *
 * @param {AgentDashboardParams} params - The query parameters for filtering dashboard data
 * @returns {Promise<AgentDashboardResponse>} The dashboard measure data
 */
const getAgentDashboard = async (
  params: AgentDashboardParams,
  signal?: AbortSignal,
): Promise<AgentDashboardResponse> => {
  try {
    const response: AxiosResponse = await axiosInstance.get(dashboardURL, { params, signal });
    return response.data;
  } catch (error: unknown) {
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching agent dashboard data.'));
    }
  }
};

/**
 * Normalizes a list params object into an axios-friendly query-string shape.
 * Arrays become comma-separated strings; `undefined`/empty values are stripped.
 */
const buildSessionListQuery = (params: AgentMinerSessionListParams): Record<string, string | number | boolean> => {
  const query: Record<string, string | number | boolean> = {
    organization_id: params.organization_id,
  };
  if (params.page != null) query.page = params.page;
  if (params.page_size != null) query.page_size = params.page_size;
  if (params.q) query.q = params.q;
  if (params.status) query.status = params.status;
  if (params.source && params.source.length > 0) query.source = params.source.join(',');
  if (params.user_id && params.user_id.length > 0) query.user_id = params.user_id.join(',');
  if (params.label && params.label.length > 0) query.label = params.label.join(',');
  if (params.start_date) query.start_date = params.start_date;
  if (params.end_date) query.end_date = params.end_date;
  if (params.has_task != null) query.has_task = params.has_task;
  // Family labels can contain spaces and dots ("Sonnet 4.5") but never commas,
  // so comma-joining round-trips cleanly through the backend's split(',').
  if (params.model_family && params.model_family.length > 0) query.model_family = params.model_family.join(',');
  if (params.min_turns != null) query.min_turns = params.min_turns;
  if (params.ordering) query.ordering = params.ordering;
  return query;
};

/**
 * Fetches a paginated list of agent sessions for the current organization.
 *
 * @param {AgentMinerSessionListParams} params - Filter, sort, and pagination params
 * @returns {Promise<AgentMinerSessionsResponse>} The page envelope with session results + SCM flag
 */
const getAgentSessions = async (params: AgentMinerSessionListParams): Promise<AgentMinerSessionsResponse> => {
  try {
    const response: AxiosResponse<AgentMinerSessionsResponse> = await axiosInstance.get(sessionsURL, {
      params: buildSessionListQuery(params),
    });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching agent sessions.'));
    }
  }
};

/**
 * Fetches the distinct sources and users that have at least one session in
 * the organization — used to populate the Source and User filter dropdowns
 * with only the values that actually appear in the sessions table.
 *
 * @param {string} organizationId - The organization whose facets to return
 * @returns {Promise<AgentMinerSessionFacets>} Arrays of available sources and users
 */
const getAgentSessionFacets = async (organizationId: string): Promise<AgentMinerSessionFacets> => {
  try {
    const response: AxiosResponse<AgentMinerSessionFacets> = await axiosInstance.get(`${sessionsURL}facets/`, {
      params: { organization_id: organizationId },
    });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching agent session facets.'));
    }
  }
};

/**
 * Fetches a single agent session with nested turns and events.
 *
 * @param {string} sessionId - The session id (UUID)
 * @returns {Promise<AgentMinerSessionDetail>} The full session detail
 */
const getAgentSession = async (sessionId: string): Promise<AgentMinerSessionDetail> => {
  try {
    const response: AxiosResponse<AgentMinerSessionDetail> = await axiosInstance.get(
      `${sessionsURL}${encodeURIComponent(sessionId)}/`,
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching the agent session.'));
    }
  }
};

/**
 * Fetches the Related-PRs attribution payload for a session (and its subtree).
 *
 * @param {string} sessionId - The session id (UUID)
 * @param {AbortSignal} signal - Optional abort signal from React Query
 * @returns {Promise<SessionPullRequestsResponse>} PRs the session fed, with survival metrics
 */
const getSessionPullRequests = async (
  sessionId: string,
  signal?: AbortSignal,
): Promise<SessionPullRequestsResponse> => {
  try {
    const response: AxiosResponse<SessionPullRequestsResponse> = await axiosInstance.get(
      `${sessionsURL}${sessionId}/pull-requests/`,
      { signal },
    );
    return response.data;
  } catch (error: unknown) {
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching session pull requests.'));
    }
  }
};

/**
 * Fetches the merged diff of one PR file with every added line attributed to the
 * agent edit that wrote it, plus each contributing edit's per-line survival.
 *
 * @param {string} sessionId - The session id (UUID)
 * @param {string} prId - The pull request (ChangeRequest) id (UUID)
 * @param {string} changeRequestFileId - The ChangeRequestFile id (UUID)
 * @param {AbortSignal} signal - Optional abort signal from React Query
 * @returns {Promise<PullRequestFileLinesResponse>} Tagged hunks and contributing edits for the file
 */
const getPullRequestFileLines = async (
  sessionId: string,
  prId: string,
  changeRequestFileId: string,
  signal?: AbortSignal,
): Promise<PullRequestFileLinesResponse> => {
  try {
    const response: AxiosResponse<PullRequestFileLinesResponse> = await axiosInstance.get(
      `${sessionsURL}${sessionId}/pull-requests/${prId}/files/${changeRequestFileId}/lines/`,
      { signal },
    );
    return response.data;
  } catch (error: unknown) {
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching pull request file lines.'));
    }
  }
};

export {
  getAgentAdoption,
  getAgentDashboard,
  getAgentSession,
  getAgentSessionFacets,
  getAgentSessions,
  getPullRequestFileLines,
  getSessionPullRequests,
  getDeliveryImpact,
  getModelComparison,
  getTaskFit,
  getTaskFitTasks,
};
